/**
 * Safe localStorage wrapper with error handling
 * Handles cases where localStorage is unavailable, quota exceeded, or throws errors
 */

export const safeStorage = {
	/**
	 * Safely get an item from localStorage
	 * Returns null if localStorage is unavailable or if the key doesn't exist
	 */
	getItem: (key: string): string | null => {
		try {
			if (typeof window === 'undefined' || !window.localStorage) {
				return null
			}
			return window.localStorage.getItem(key)
		} catch (error) {
			console.warn(`Failed to get item from localStorage: ${key}`, error)
			return null
		}
	},

	/**
	 * Safely set an item in localStorage
	 * Returns true if successful, false otherwise
	 */
	setItem: (key: string, value: string): boolean => {
		try {
			if (typeof window === 'undefined' || !window.localStorage) {
				return false
			}
			window.localStorage.setItem(key, value)
			return true
		} catch (error) {
			console.warn(`Failed to set item in localStorage: ${key}`, error)
			// If quota exceeded or other error, try to clear old data
			if (error instanceof DOMException && error.name === 'QuotaExceededError') {
				console.warn('localStorage quota exceeded, attempting to clear old data')
				try {
					// Clear stale program selections that might be taking up space
					const keysToRemove: string[] = []
					for (let i = 0; i < window.localStorage.length; i++) {
						const storageKey = window.localStorage.key(i)
						if (storageKey?.startsWith('old_') || storageKey?.startsWith('stale_')) {
							keysToRemove.push(storageKey)
						}
					}
					keysToRemove.forEach(k => window.localStorage.removeItem(k))
					// Try again
					window.localStorage.setItem(key, value)
					return true
				} catch {
					return false
				}
			}
			return false
		}
	},

	/**
	 * Safely remove an item from localStorage
	 * Returns true if successful, false otherwise
	 */
	removeItem: (key: string): boolean => {
		try {
			if (typeof window === 'undefined' || !window.localStorage) {
				return false
			}
			window.localStorage.removeItem(key)
			return true
		} catch (error) {
			console.warn(`Failed to remove item from localStorage: ${key}`, error)
			return false
		}
	},

	/**
	 * Check if localStorage is available
	 */
	isAvailable: (): boolean => {
		try {
			if (typeof window === 'undefined' || !window.localStorage) {
				return false
			}
			const testKey = '__localStorage_test__'
			window.localStorage.setItem(testKey, 'test')
			window.localStorage.removeItem(testKey)
			return true
		} catch {
			return false
		}
	},

	/**
	 * Set onboarding completion flag to prevent redirect loops
	 */
	setOnboardingComplete: (userId: string): boolean => {
		return safeStorage.setItem(`onboarding_complete_${userId}`, 'true')
	},

	/**
	 * Check if onboarding is complete for a user
	 */
	isOnboardingComplete: (userId: string): boolean => {
		const value = safeStorage.getItem(`onboarding_complete_${userId}`)
		return value === 'true'
	},

	/**
	 * Clear onboarding completion flag (e.g., when user logs out)
	 */
	clearOnboardingComplete: (userId: string): boolean => {
		return safeStorage.removeItem(`onboarding_complete_${userId}`)
	},
}
